import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { ShipmentTemplate } from '~api/userprofile';

import { useMemo } from 'react';

import { UserProfileApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { useShippingMethodListSuspenseQuery } from '~src/main/queries';
import { useCarrierAddonsQuery } from '~src/pages/shipment/queries';
import { createContactFromAddress } from '~src/utils/create-contact-from-address';

import { useShipmentTemplateListFilter } from './list/filters/useShipmentTemplateListFilter';
import { ShipmentTemplateDetails } from './types';

export function useShipmentTemplateQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['shipmentTemplates', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useSiteTagQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['siteTags', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
  };

  return keys;
}

export function useShipmentTemplatesList() {
  const queryKey = useShipmentTemplateQueryKey();

  return useQuery({
    queryFn: async () => {
      const {
        data: { tags },
      } = await UserProfileApiInstance.userTagsList.userProfileListUserTags();

      const { data } = await UserProfileApiInstance.shipmentTemplatesList.userProfileListShipmentTemplates({
        tag_ids: tags?.map(tag => tag.id!),
      });

      return data.shipment_templates_with_tags ?? [];
    },
    queryKey: queryKey.lists(),
  });
}

export function useFilteredShipmentTemplateList() {
  const { search } = useShipmentTemplateListFilter();
  const { data = [], ...query } = useShipmentTemplatesList();

  const filtered = useMemo(() => {
    return data.filter(template => {
      const lowerCaseSearchQuery = search.toLocaleLowerCase();

      if (!lowerCaseSearchQuery) {
        return true;
      }

      /**
       * Search by full or partial name.
       */
      return (template.template?.data?.name ?? '').toLocaleLowerCase().includes(lowerCaseSearchQuery);
    });
  }, [data, search]);

  return {
    filtered,
    data,
    ...query,
  };
}

export function useTemplateDetailsQuery({ id }: Pick<ShipmentTemplate, 'id'>): ShipmentTemplateDetails {
  const queryKey = useShipmentTemplateQueryKey();

  const {
    data: { template, tags },
  } = useSuspenseQuery({
    queryFn: async () => {
      const { data } = await UserProfileApiInstance.shipmentTemplatesGet.userProfileGetShipmentTemplate({ id });
      return data;
    },
    queryKey: queryKey.detail(id),
  });

  const { data: shippingMethods } = useShippingMethodListSuspenseQuery({ customBookingMethods: true });
  const { data: addonsData } = useCarrierAddonsQuery();

  return {
    id: template?.id,
    addons: template?.data?.addons?.map(addon => ({
      ...addon,
      description: addonsData?.addons?.find(a => a.code === addon.code)?.description,
    })),
    contact_from: createContactFromAddress('template', 'contact_from', template?.data?.address_from),
    contact_to: createContactFromAddress('template', 'contact_to', template?.data?.address_to),
    contact_return: createContactFromAddress('template', 'contact_return', template?.data?.address_return),
    customer_info: {
      ...template?.data?.customer_info,
      contact: createContactFromAddress(
        'template',
        'contact_customer_info',
        template?.data?.customer_info?.address,
        template?.data?.customer_info?.phone,
        template?.data?.customer_info?.email
      ),
    },
    contents: template?.data?.contents,
    courier_instructions: template?.data?.courier_instructions,
    dimensions: template?.data?.dimensions,
    meta: template?.data?.meta,
    name: template?.data?.name,
    number_of_parcels: template?.data?.number_of_parcels,
    shipment_value: template?.data?.shipment_value,
    shipping_method: shippingMethods.find(s => s.id === template?.data?.shipping_method),
    weight: template?.data?.weight,
    // tag_ids: tags?.map((tag) => tag.id!),
    tags: tags,
  };
}

export function useSiteTagListQuery() {
  const keys = useSiteTagQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await UserProfileApiInstance.tagsList.userProfileListTags();
      return data.tags?.filter(t => !t.user_id);
    },
    queryKey: keys.lists(),
  });
}
